import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MeetingCameraIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import useRenewClientToken from '@rsos/hooks/videoTo911/useRenewClientToken';
import useSelectedVideoRoom from '@rsos/hooks/videoTo911/useSelectedVideoRoom';
import {
  LIVE_VIDEO_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import useIsVT911Enabled from '../../../../hooks/useIsVT911Enabled';
import { IconButton } from './VideoTo911.styles';
import { useVideoSessionInitialization } from './useVideoSessionInitialization/useVideoSessionInitializationLegacy';

const VTIconsRow = ({ isChatBoxVisible, onStartConversation }) => {
  const dispatch = useDispatch();
  const selectedCallerId = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const isVT911Enabled = useIsVT911Enabled();
  const tf911Conversation =
    useSelectedTextFrom911Conversation(selectedCallerId);

  const selectedVideoRoom = useSelectedVideoRoom(selectedCallerId);

  const isConversationValid = useMemo(
    () =>
      tf911Conversation?.data &&
      Object.keys(tf911Conversation?.data).length > 0,
    [tf911Conversation],
  );

  const isConversationEnded = useMemo(
    () => tf911Conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [tf911Conversation],
  );

  const isAnyLoading = useMemo(() => {
    if (tf911Conversation) {
      return Object.values(tf911Conversation?.loading).some(value => !!value);
    }
    return false;
  }, [tf911Conversation]);

  useRenewClientToken({
    roomId: selectedVideoRoom?.room_id,
    refreshToken: selectedVideoRoom?.refresh_token,
  });

  const { handleInitVideoSession } = useVideoSessionInitialization({
    isForOverview: false,
    selectedVideoRoom,
    onStartConversation,
  });

  useEffect(() => {
    // Close the LiveVideoCard whenever selectedCallerID changes
    dispatch(closeCard(LIVE_VIDEO_CARD));
    return () => {
      dispatch(closeCard(LIVE_VIDEO_CARD));
    };
  }, [dispatch, selectedCallerId]);

  if (!isVT911Enabled) {
    return null;
  }

  return (
    <Box
      borderTop="1px solid"
      borderColor="lightLineDivider"
      justify="flex-end"
      height={isChatBoxVisible ? 'auto' : 0}
      horizontal
      overflowY={isChatBoxVisible ? 'auto' : 'hidden'}
    >
      <IconButton
        btnType="tertiary"
        disabled={isAnyLoading}
        onClick={handleInitVideoSession}
        size="xxsmall"
      >
        <MeetingCameraIcon
          height="12px"
          width="12px"
          color={
            !isConversationValid || isConversationEnded || isAnyLoading
              ? 'secondaryText'
              : 'lightDanger'
          }
        />
      </IconButton>
    </Box>
  );
};

export default VTIconsRow;
